import * as sdk from 'microsoft-cognitiveservices-speech-sdk';
import { AvatarManager } from "@src/js/avatar-managers/AvatarManager";
import config from "@src/js/config";
export class ReadyPlayerAzureAssistantManager extends AvatarManager {
    constructor(avatarElement, avatarUrl, azureRegion, lipsyncType = 'visemes', voiceName = 'de-DE-KatjaNeural') {
        super();
        this.avatarUrl = avatarUrl;
        this.azureRegion = azureRegion;
        this.lipsyncType = lipsyncType;
        this.voiceName = voiceName;
        this.microsoftSynthesizer = null;
        this.prevViseme = null;
        this.speaking = false;
        this.interrupted = false; // Flag to track if current speech was interrupted
        this.visemesbuffer = {
            visemes: [],
            vtimes: [],
            vdurations: [],
        };
        this.wordsbuffer = {
            words: [],
            wtimes: [],
            wdurations: []
        };
        this.azureBlendShapes = {
            frames: [],
            sbuffer: [],
            orderBuffer: {}
        };
        this.visemeMap = [
            /* 0  */ "sil", // Silence
            /* 1  */ "aa", // æ, ə, ʌ
            /* 2  */ "aa", // ɑ
            /* 3  */ "O", // ɔ
            /* 4  */ "E", // ɛ, ʊ
            /* 5  */ "RR", // ɝ
            /* 6  */ "I", // j, i, ɪ
            /* 7  */ "U", // w, u
            /* 8  */ "O", // o
            /* 9  */ "O", // aʊ
            /* 10 */ "O", // ɔɪ
            /* 11 */ "I", // aɪ
            /* 12 */ "kk", // h
            /* 13 */ "RR", // ɹ
            /* 14 */ "nn", // l
            /* 15 */ "SS", // s, z
            /* 16 */ "CH", // ʃ, tʃ, dʒ, ʒ
            /* 17 */ "TH", // ð
            /* 18 */ "FF", // f, v
            /* 19 */ "DD", // d, t, n, θ
            /* 20 */ "kk", // k, g, ŋ
            /* 21 */ "PP" // p, b, m
        ];
        this.AzureBlendshapeMap = [
            /* 0  */ "eyeBlinkLeft",
            /* 1  */ "eyeLookDownLeft",
            /* 2  */ "eyeLookInLeft",
            /* 3  */ "eyeLookOutLeft",
            /* 4  */ "eyeLookUpLeft",
            /* 5  */ "eyeSquintLeft",
            /* 6  */ "eyeWideLeft",
            /* 7  */ "eyeBlinkRight",
            /* 8  */ "eyeLookDownRight",
            /* 9  */ "eyeLookInRight",
            /* 10 */ "eyeLookOutRight",
            /* 11 */ "eyeLookUpRight",
            /* 12 */ "eyeSquintRight",
            /* 13 */ "eyeWideRight",
            /* 14 */ "jawForward",
            /* 15 */ "jawLeft",
            /* 16 */ "jawRight",
            /* 17 */ "jawOpen",
            /* 18 */ "mouthClose",
            /* 19 */ "mouthFunnel",
            /* 20 */ "mouthPucker",
            /* 21 */ "mouthLeft",
            /* 22 */ "mouthRight",
            /* 23 */ "mouthSmileLeft",
            /* 24 */ "mouthSmileRight",
            /* 25 */ "mouthFrownLeft",
            /* 26 */ "mouthFrownRight",
            /* 27 */ "mouthDimpleLeft",
            /* 28 */ "mouthDimpleRight",
            /* 29 */ "mouthStretchLeft",
            /* 30 */ "mouthStretchRight",
            /* 31 */ "mouthRollLower",
            /* 32 */ "mouthRollUpper",
            /* 33 */ "mouthShrugLower",
            /* 34 */ "mouthShrugUpper",
            /* 35 */ "mouthPressLeft",
            /* 36 */ "mouthPressRight",
            /* 37 */ "mouthLowerDownLeft",
            /* 38 */ "mouthLowerDownRight",
            /* 39 */ "mouthUpperUpLeft",
            /* 40 */ "mouthUpperUpRight",
            /* 41 */ "browDownLeft",
            /* 42 */ "browDownRight",
            /* 43 */ "browInnerUp",
            /* 44 */ "browOuterUpLeft",
            /* 45 */ "browOuterUpRight",
            /* 46 */ "cheekPuff",
            /* 47 */ "cheekSquintLeft",
            /* 48 */ "cheekSquintRight",
            /* 49 */ "noseSneerLeft",
            /* 50 */ "noseSneerRight",
            /* 51 */ "tongueOut",
            /* 52 */ "headRotateZ",
            /* 53 */ // "leftEyeRoll", // Not supported
            /* 54 */ // "rightEyeRoll" // Not supported
        ];
        this.nodeAvatar = avatarElement;
    }
    async init() {
        //@ts-ignore
        if (!window.TalkingHead) {
            console.error('[TalkingHeadAvatarManager] TalkingHead is not loaded from global scope.');
            return;
        }
        //@ts-ignore
        this.head = new window.TalkingHead(this.nodeAvatar, {
            ttsEndpoint: "/gtts/",
            cameraView: "upper",
            lipsyncLang: "en"
        });
        try {
            await this.head.showAvatar({
                url: this.avatarUrl,
                body: 'F',
            });
        }
        catch (error) {
            console.error('[TalkingHeadAvatarManager] Load error:', error);
        }
    }
    textToSSML(text) {
        return `
          <speak version="1.0" xmlns:mstts="http://www.w3.org/2001/mstts" xml:lang="en-US">
            <voice name="${this.voiceName}">
              <mstts:viseme type="FacialExpression" />
              ${text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')}
            </voice>
          </speak>`;
    }
    async speak(text) {
        console.log("[Assistant] Speak called with text:", text.substring(0, 50) + "...");
        console.log("[Assistant] Current speaking state:", this.speaking);
        console.log("[Assistant] Synthesizer exists:", !!this.microsoftSynthesizer);
        const ssml = this.textToSSML(text);
        await this.azureSpeak(ssml);
    }
    azureSpeak(ssml) {
        return new Promise(async (resolve, reject) => {
            if (!this.microsoftSynthesizer) {
                try {
                    await this.initializeSynthesizer();
                }
                catch (error) {
                    reject("Failed to initialize Azure Speech synthesizer");
                    return;
                }
            }
            // Reset interrupted flag for new speech
            this.interrupted = false;
            let playbackEnded = false;
            // Start stream playback
            this.head.streamStart({ sampleRate: 48000, mood: "happy", gain: 0.5, lipsyncType: this.lipsyncType }, () => {
                console.log("Audio playback started.");
                if (!this.interrupted) {
                    this.speaking = true;
                }
            }, () => {
                console.log("Audio playback ended.");
                this.speaking = false;
                playbackEnded = true;
                resolve();
            });
            // Begin TTS synthesis
            this.microsoftSynthesizer.speakSsmlAsync(ssml, (result) => {
                // Check if we were interrupted during synthesis
                if (this.interrupted) {
                    console.log("Speech was interrupted during synthesis");
                    resolve();
                    return;
                }
                if (result.reason === sdk.ResultReason.SynthesizingAudioCompleted) {
                    if (this.lipsyncType === "visemes" && this.prevViseme) {
                        const finalDuration = 100;
                        this.visemesbuffer.visemes.push(this.prevViseme.viseme);
                        this.visemesbuffer.vtimes.push(this.prevViseme.vtime);
                        this.visemesbuffer.vdurations.push(finalDuration);
                        this.prevViseme = null;
                    }
                    let speak = {};
                    if (this.lipsyncType === "visemes" && this.visemesbuffer.visemes.length) {
                        speak.visemes = this.visemesbuffer.visemes.splice(0);
                        speak.vtimes = this.visemesbuffer.vtimes.splice(0);
                        speak.vdurations = this.visemesbuffer.vdurations.splice(0);
                    }
                    if (this.lipsyncType === "blendshapes") {
                        speak.anims = this.azureBlendShapes.sbuffer.splice(0);
                    }
                    speak.words = this.wordsbuffer.words.splice(0);
                    speak.wtimes = this.wordsbuffer.wtimes.splice(0);
                    speak.wdurations = this.wordsbuffer.wdurations.splice(0);
                    if (speak.visemes || speak.words || speak.anims) {
                        speak.audio = new ArrayBuffer(0);
                        this.head.streamAudio(speak);
                    }
                    this.head.streamNotifyEnd();
                    this.resetLipsyncBuffers();
                }
                else {
                    if (!playbackEnded && !this.interrupted)
                        reject("Azure synthesis failed.");
                }
            }, (err) => {
                console.error("Azure speech synthesis error:", err);
                this.resetLipsyncBuffers();
                this.speaking = false;
                if (!playbackEnded && !this.interrupted)
                    reject(err);
            });
        });
    }
    async initializeSynthesizer() {
        try {
            const regionValue = this.azureRegion;
            // Fetch temporary token from your server
            const tokenResponse = await fetch(`${config.API_URL}chat/azure/speech-token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            if (!tokenResponse.ok) {
                throw new Error('Failed to fetch Azure Speech token');
            }
            const { token } = await tokenResponse.json();
            // Use fromAuthorizationToken instead of fromSubscription
            const speechConfig = sdk.SpeechConfig.fromAuthorizationToken(token, regionValue);
            speechConfig.speechSynthesisOutputFormat =
                sdk.SpeechSynthesisOutputFormat.Raw48Khz16BitMonoPcm;
            this.microsoftSynthesizer = new sdk.SpeechSynthesizer(speechConfig, null);
            // Set up event handlers
            this.setupSynthesizerEventHandlers();
        }
        catch (error) {
            console.error("Failed to initialize Azure Speech synthesizer:", error);
            throw error;
        }
    }
    setupSynthesizerEventHandlers() {
        if (!this.microsoftSynthesizer)
            return;
        this.microsoftSynthesizer.synthesizing = (s, e) => {
            // Skip processing if we're interrupted
            if (this.interrupted) {
                console.log("Skipping audio stream - interrupted");
                return;
            }
            switch (this.lipsyncType) {
                case "blendshapes":
                    this.head.streamAudio({
                        audio: e.result.audioData,
                        anims: this.azureBlendShapes.sbuffer.splice(0),
                    });
                    break;
                case "visemes":
                    this.head.streamAudio({
                        audio: e.result.audioData,
                        visemes: this.visemesbuffer.visemes.splice(0),
                        vtimes: this.visemesbuffer.vtimes.splice(0),
                        vdurations: this.visemesbuffer.vdurations.splice(0),
                    });
                    break;
                case "words":
                    this.head.streamAudio({
                        audio: e.result.audioData,
                        words: this.wordsbuffer.words.splice(0),
                        wtimes: this.wordsbuffer.wtimes.splice(0),
                        wdurations: this.wordsbuffer.wdurations.splice(0),
                    });
                    break;
                default:
                    console.error(`Unknown animation mode: ${this.lipsyncType}`);
            }
        };
        this.microsoftSynthesizer.visemeReceived = (s, e) => {
            // Skip processing if we're interrupted
            if (this.interrupted)
                return;
            if (this.lipsyncType === "visemes") {
                const vtime = e.audioOffset / 10000;
                const viseme = this.visemeMap[e.visemeId];
                if (!this.head.isStreaming)
                    return;
                if (this.prevViseme) {
                    let vduration = vtime - this.prevViseme.vtime;
                    if (vduration < 40)
                        vduration = 40;
                    this.visemesbuffer.visemes.push(this.prevViseme.viseme);
                    this.visemesbuffer.vtimes.push(this.prevViseme.vtime);
                    this.visemesbuffer.vdurations.push(vduration);
                }
                this.prevViseme = { viseme, vtime };
            }
            else if (this.lipsyncType === "blendshapes") {
                let animation = null;
                if (e?.animation?.trim()) {
                    try {
                        animation = JSON.parse(e.animation);
                    }
                    catch (err) {
                        console.error("Error parsing blendshapes:", err);
                        return;
                    }
                }
                if (!animation)
                    return;
                const vs = {};
                this.AzureBlendshapeMap.forEach((mtName, i) => {
                    vs[mtName] = animation.BlendShapes.map((frame) => frame[i]);
                });
                this.azureBlendShapes.sbuffer.push({
                    name: "blendshapes",
                    delay: animation.FrameIndex * 1000 / 60,
                    dt: Array.from({ length: animation.BlendShapes.length }, () => 1000 / 60),
                    vs: vs,
                });
            }
        };
        this.microsoftSynthesizer.wordBoundary = (s, e) => {
            // Skip processing if we're interrupted
            if (this.interrupted)
                return;
            const word = e.text;
            const time = e.audioOffset / 10000;
            const duration = e.duration / 10000;
            if (e.boundaryType === "PunctuationBoundary" && this.wordsbuffer.words.length) {
                this.wordsbuffer.words[this.wordsbuffer.words.length - 1] += word;
                this.wordsbuffer.wdurations[this.wordsbuffer.wdurations.length - 1] += duration;
            }
            else {
                this.wordsbuffer.words.push(word);
                this.wordsbuffer.wtimes.push(time);
                this.wordsbuffer.wdurations.push(duration);
            }
        };
    }
    resetLipsyncBuffers() {
        this.visemesbuffer = {
            visemes: [],
            vtimes: [],
            vdurations: [],
        };
        this.prevViseme = null;
        this.wordsbuffer = {
            words: [],
            wtimes: [],
            wdurations: []
        };
        this.azureBlendShapes = {
            frames: [],
            sbuffer: [],
            orderBuffer: {}
        };
    }
    async interruptSpeaking() {
        if (this.speaking) {
            console.log("[Assistant] Interrupting speech.");
            // Immediately set speaking to false to prevent race conditions
            this.speaking = false;
            // Stop current talking-head playback and animation
            if (this.head?.isStreaming) {
                try {
                    // Stop the audio stream
                    if (typeof this.head.streamStop === "function") {
                        this.head.streamStop();
                    }
                    // Force stop any ongoing animation
                    if (typeof this.head.stopSpeaking === "function") {
                        this.head.stopSpeaking();
                    }
                    // Reset to idle pose
                    if (typeof this.head.setMood === "function") {
                        this.head.setMood("neutral");
                    }
                    this.head.streamNotifyEnd();
                }
                catch (err) {
                    console.warn("Failed to stop head stream/animation:", err);
                }
            }
            // Cancel any ongoing synthesis immediately
            if (this.microsoftSynthesizer) {
                try {
                    // Close and recreate synthesizer for immediate stop
                    // this.microsoftSynthesizer.close();
                    // this.microsoftSynthesizer = null;
                    // // Reinitialize synthesizer for next use
                    // await this.initializeSynthesizer();
                }
                catch (err) {
                    console.warn("Failed to cancel synthesis:", err);
                }
            }
            // Reset buffers and state immediately
            this.resetLipsyncBuffers();
            console.log("[Assistant] Speech interruption complete - ready for new input.");
        }
    }
    isSpeaking() {
        return this.speaking;
    }
    destroy() {
        this.nodeAvatar.innerHTML = '';
        this.microsoftSynthesizer?.close();
        this.microsoftSynthesizer = null;
    }
}
